import React, { useRef, useState } from "react";
import WordMorphology from "../WordMorphology";
import { track } from "../../utils/analytics";
import { PDF_SIM } from "./pdfSimContent";

/**
 * OnboardingPdfSim — the PDF-reader taste for classical_texts-motivated
 * funnel visitors. Mirrors Reading.js's real interface: the light scanned
 * page inside the dark app, the amber "◇ Iʿrāb mode" toggle, the guided
 * tap-first-word / tap-last-word sentence selection, the Analyze beat,
 * then tokb-* color-tinted words that open the word-structure modal.
 * Everything is canned (bundled ḥadīth text + hand-checked morphology) —
 * zero cost, zero quota, nothing to fail for anonymous visitors.
 */
export default function OnboardingPdfSim({ onContinue }) {
  const words = PDF_SIM.words;
  const [irabMode, setIrabMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [popover, setPopover] = useState(null); // word (translate mode)
  const [structureWord, setStructureWord] = useState(null);
  // Coach cues, one target at a time: first word → Structure → Iʿrāb mode →
  // first word of range → last word → Analyze → a tinted word.
  const [popoverSeen, setPopoverSeen] = useState(false);
  const [structSeen, setStructSeen] = useState(false);
  const [irabStructSeen, setIrabStructSeen] = useState(false);
  const timerRef = useRef(null);
  const tapped = useRef(false);

  const toggleIrabMode = () => {
    setIrabMode((v) => !v);
    setPopover(null);
    setRangeStart(null);
    setRangeEnd(null);
    setAnalyzed(false);
    track("onboarding-pdf-irab-toggle", {});
  };

  const tapWord = (i) => {
    const word = words[i];
    if (!irabMode) {
      setPopover(popover?.key === word.key ? null : word);
      setPopoverSeen(true);
      if (!tapped.current) {
        tapped.current = true;
        track("onboarding-pdf-word-tap", {});
      }
      return;
    }
    if (analyzed) {
      if (word.morphology) setStructureWord(word.morphology);
      setIrabStructSeen(true);
      track("onboarding-pdf-structure", {});
      return;
    }
    if (rangeStart == null) setRangeStart(i);
    else if (rangeEnd == null) setRangeEnd(Math.max(i, rangeStart));
  };

  const analyze = () => {
    setAnalyzing(true);
    track("onboarding-pdf-analyze", {});
    timerRef.current = setTimeout(() => {
      setAnalyzing(false);
      setAnalyzed(true);
    }, 900);
  };

  const inRange = (i) =>
    rangeStart != null && i >= rangeStart && (rangeEnd == null ? i === rangeStart : i <= rangeEnd);

  const wordCls = (i) => {
    if (irabMode && analyzed && inRange(i)) {
      const kind = words[i].kind;
      return kind ? `tokb-${kind} cursor-pointer` : "bg-amber-200/40 cursor-pointer";
    }
    if (irabMode && inRange(i)) return "bg-amber-200/60";
    if (irabMode) return "cursor-pointer hover:bg-amber-200/40";
    return "cursor-pointer hover:bg-amber-200/40";
  };

  // Which word (if any) carries the coach pulse right now.
  const coachWord = (i) => {
    if (!irabMode) return !popoverSeen && i === 0;
    if (analyzed) return !irabStructSeen && i === rangeStart;
    if (rangeStart == null) return i === 0;
    if (rangeEnd == null) return i === words.length - 1;
    return false;
  };

  const hint =
    !irabMode
      ? "Tap any word for its meaning."
      : analyzed
        ? "Tap a colored word for its structure and iʿrāb."
        : rangeStart == null
          ? "Tap the first word of the sentence"
          : rangeEnd == null
            ? "Now tap the last word"
            : `${rangeEnd - rangeStart + 1} words selected`;

  return (
    <div className="w-full">
      <h2 className="text-xl sm:text-2xl font-semibold text-dt-parchment mb-1.5">
        Bring your own books
      </h2>
      <p className="text-sm text-dt-parchment-dim mb-4">{PDF_SIM.intro}</p>

      {/* ── The real Reading interface, scripted (light page in the dark app).
            Inline parchment/ink colors throughout: .dark-scope remaps light
            Tailwind classes (bg-white, text-ink-800…) to dark equivalents,
            but this card is deliberately a light paper surface. ── */}
      <div className="rounded-2xl overflow-hidden text-left" style={{ border: "1px solid var(--panel-line)", background: "var(--parchment)" }}>
        {/* Reader header: file + page (mirrors the reader's chrome) */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100">
          {/* Inline colors here and below: .dark-scope remaps text-ink-600/800
              to parchment (theme.css), but this card is a LIGHT surface inside
              the dark funnel — inline style wins over the remap. */}
          <span dir="rtl" lang="ar" className="font-arabic text-sm truncate" style={{ color: "var(--ink)" }}>
            📕 {PDF_SIM.fileName}
          </span>
          <span className="text-[11px] text-ink-400 shrink-0">{PDF_SIM.pageLabel}</span>
        </div>

        {/* Selection toolbar — as in Reading.js (topic button decorative) */}
        <div className="flex flex-wrap items-center justify-center gap-2 py-2 border-b border-slate-100 text-sm">
          <span className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-400 text-xs font-medium select-none">
            Select for topic
          </span>
          <button
            onClick={toggleIrabMode}
            title="Select a sentence and get its full word-by-word iʿrāb"
            className={`px-2.5 py-1 rounded-lg border text-xs font-medium ${
              irabMode ? "bg-amber-600 text-white border-amber-600" : "border-amber-400 text-amber-600 hover:bg-amber-400/15"
            } ${structSeen && !irabMode ? "coach-pulse" : ""}`}
          >
            ◇ {irabMode ? "Iʿrāb mode on" : "Iʿrāb mode"}
          </button>
        </div>

        {/* Iʿrāb-mode hint bar — the reader's guided range selection */}
        <div className="px-4 py-2 border-b border-slate-100">
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <span style={{ color: "var(--ink)" }}>{hint}</span>
            {irabMode && !analyzed && rangeEnd != null && (
              <button
                onClick={analyze}
                disabled={analyzing}
                className={`px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-40 ${
                  analyzing ? "" : "coach-pulse"
                }`}
              >
                {analyzing ? "Analyzing…" : "Analyze — أَعْرِب"}
              </button>
            )}
          </div>
        </div>

        {/* The scanned page */}
        <div className="px-5 py-6">
          <p dir="rtl" lang="ar" className="text-center text-[13px] text-ink-400 mb-3 font-arabic">
            {PDF_SIM.attribution}
          </p>
          <p dir="rtl" lang="ar" className="font-arabic text-[26px] leading-[2.4] text-justify" style={{ color: "var(--ink)" }}>
            {words.map((w, i) => (
              <React.Fragment key={w.key}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => tapWord(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tapWord(i); }
                  }}
                  className={`rounded px-0.5 -mx-0.5 transition-colors ${wordCls(i)} ${coachWord(i) ? "coach-pulse" : ""}`}
                >
                  {w.text}
                </span>{" "}
              </React.Fragment>
            ))}
          </p>

          {/* Translate popover — the reader's tap-a-word card */}
          {popover && !irabMode && (
            <div className="mt-4 mx-auto max-w-xs rounded-xl shadow-lg border border-slate-200 px-3.5 py-3 animate-rise" style={{ background: "var(--parchment)" }}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p dir="rtl" lang="ar" className="font-arabic text-xl leading-snug" style={{ color: "var(--ink)" }}>{popover.text}</p>
                  <p className="text-sm font-medium" style={{ color: "var(--ink)", opacity: 0.75 }}>{popover.gloss}</p>
                  <p className="text-xs text-ink-400 italic">{popover.translit}</p>
                </div>
                <button
                  onClick={() => {
                    setStructureWord(popover.morphology);
                    setStructSeen(true);
                    track("onboarding-pdf-structure", {});
                  }}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg border border-amber-400 text-amber-600 hover:bg-amber-400/15 text-xs font-medium ${
                    structSeen ? "" : "coach-pulse"
                  }`}
                >
                  ◇ Structure
                </button>
              </div>
            </div>
          )}

          {analyzed && (
            <p className="text-center text-xs text-ink-500 mt-4">{PDF_SIM.sentenceTranslation}</p>
          )}
        </div>
      </div>

      <p className="text-xs text-dt-parchment-dim mt-3">
        {analyzed
          ? "Works on any Arabic PDF you upload — scanned or typed, one page at a time."
          : irabMode
            ? "The reader analyzes any sentence you select, word by word."
            : "Try Iʿrāb mode — then select the sentence from إِنَّمَا to نَوَى."}
      </p>

      {/* Never gate the funnel on playing along. */}
      <button
        type="button"
        onClick={onContinue}
        className="w-full mt-5 px-4 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
      >
        {analyzed ? "I want this for my books" : "Continue"}
      </button>

      {structureWord && (
        <WordMorphology word={structureWord} onClose={() => setStructureWord(null)} />
      )}
    </div>
  );
}
